import type {
  Comment,
  Reply,
} from '@getaccept/dsr-shared-new/src/contextual-commenting/types/comment';
import { CommentStatus } from '@getaccept/dsr-shared-new/src/contextual-commenting/types/comment-status';
import type { CreateCommentDto } from '@getaccept/dsr-shared-new/src/contextual-commenting/types/create-comment-dto';
import type { CreateReplyInput } from '@getaccept/dsr-shared-new/src/contextual-commenting/types/create-reply-input';
import type { SetContextualCommentAndRepliesReadByInput } from '@getaccept/dsr-shared-new/src/contextual-commenting/types/set-contextual-comment-and-replies-read-by-input';
import { GraphQLFetchPolicy } from '@getaccept/lib-shared-new/src/graphql/graphql-fetch-policy';
import type { UpdateCommentDto } from '@getaccept/dsr-shared-new/src/contextual-commenting/types/update-comment-dto';
import { CommentsMutations } from '../graphql/comments.mutations';
import { CommentsQueries } from '../graphql/comments.queries';
import { apolloClient } from '../../../apollo';

export class CommentsService {
  static async create(payload: CreateCommentDto): Promise<Comment> {
    const {
      data: { createDealroomContextualComment },
    } = await apolloClient.mutate({
      mutation: CommentsMutations.create,
      variables: {
        payload,
      },
    });
    return createDealroomContextualComment;
  }

  static async load(
    statuses: CommentStatus[] = [CommentStatus.Draft, CommentStatus.Active, CommentStatus.Resolved],
    limit?: number,
    offset?: number
  ) {
    const {
      data: { dealroomContextualComments },
    } = await apolloClient.query({
      query: CommentsQueries.load,
      variables: {
        statuses,
        limit,
        offset,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return dealroomContextualComments;
  }

  static async loadOne(commentId: string): Promise<Comment> {
    const {
      data: { dealroomContextualComment },
    } = await apolloClient.query({
      query: CommentsQueries.loadOne,
      variables: {
        commentId,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return dealroomContextualComment;
  }

  static async update(payload: Partial<UpdateCommentDto>, commentId: string): Promise<void> {
    await apolloClient.mutate({
      mutation: CommentsMutations.update,
      variables: {
        payload,
        commentId,
      },
    });
  }

  static async createReply(payload: Partial<CreateReplyInput>, commentId: string): Promise<Reply> {
    const {
      data: { addDealroomContextualCommentReply },
    } = await apolloClient.mutate({
      mutation: CommentsMutations.createReply,
      variables: {
        payload,
        commentId,
      },
    });

    return addDealroomContextualCommentReply;
  }

  static async setReadBy(payload: SetContextualCommentAndRepliesReadByInput): Promise<void> {
    await apolloClient.mutate({
      mutation: CommentsMutations.setReadBy,
      variables: { payload },
    });
  }

  static async delete(commentId: string) {
    await apolloClient.mutate({
      mutation: CommentsMutations.delete,
      variables: {
        commentId,
      },
    });
  }
}
